import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import "@awesome.me/webawesome/dist/components/drawer/drawer.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/divider/divider.js";
import type { UserRole } from "../services/auth";
import type { SyncState } from "../services/sync";

/**
 * Slide-out navigation drawer.
 *
 * Holds every navigation destination that isn't pinned to the bottom nav.
 * On large screens app-shell renders this with `persistent` so it stays open
 * as a sidebar; on phone/tablet it's toggled by the header hamburger.
 *
 * Settings is intentionally NOT in this drawer — it lives behind the profile
 * menu in the header.
 */
@customElement("app-drawer")
export class AppDrawer extends LitElement {
  @property({ type: Boolean, reflect: true }) open = false;
  @property({ type: Boolean }) persistent = false;
  @property({ type: String }) currentPath = "/";
  @property({ type: Array }) roles: UserRole[] = [];
  @property({ type: Boolean }) isAdmin = false;
  @property({ type: String }) syncStatus: SyncState["status"] = "idle";
  @property({ attribute: false }) lastSyncTime?: Date;
  @property({ type: Number }) pendingChanges = 0;

  static styles = css`
    :host {
      display: contents;
    }

    /* Persistent (desktop) sidebar mode — render inline, not as overlay */
    :host([persistent]) .persistent-shell {
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      width: 240px;
      background: white;
      border-right: 1px solid #dee2e6;
      display: flex;
      flex-direction: column;
      z-index: 1050;
      padding-top: env(safe-area-inset-top);
    }

    .brand {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: calc(env(safe-area-inset-top) + 0.5rem) 0.75rem 0.5rem;
      font-weight: 700;
      font-size: 1.25rem;
      color: var(--color-accent, #bd64fc);
      border-bottom: 1px solid var(--color-border, #dee2e6);
      min-height: calc(env(safe-area-inset-top) + 3.5rem);
    }

    .brand .close-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      cursor: pointer;
      color: #495057;
      padding: 0.5rem;
      border-radius: 0.5rem;
    }

    .brand .close-btn:hover {
      background: #f1f3f5;
    }

    .brand .close-btn wa-icon {
      font-size: 1.25rem;
    }

    /* Hide close button when persistent (desktop) */
    :host([persistent]) .brand .close-btn {
      display: none;
    }

    :host([persistent]) .brand {
      padding: 1rem 1.25rem;
      min-height: 0;
    }

    .nav-list {
      flex: 1;
      overflow-y: auto;
      padding: 0.5rem 0;
    }

    .nav-list a {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem 1.25rem;
      color: #495057;
      text-decoration: none;
      font-size: 0.9rem;
      cursor: pointer;
      transition: background 0.15s;
    }

    .nav-list a:hover {
      background: var(--color-accent-soft, #faf6ff);
      color: var(--color-accent, #bd64fc);
    }

    .nav-list a.active {
      background: var(--color-accent-soft, #faf6ff);
      color: var(--color-accent, #bd64fc);
      font-weight: 600;
    }

    .nav-list a wa-icon {
      font-size: 1.15rem;
      width: 1.25rem;
      text-align: center;
    }

    wa-divider {
      margin: 0.5rem 0;
    }

    .footer {
      border-top: 1px solid #dee2e6;
      padding: 0.75rem 1.25rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.8rem;
      color: #6c757d;
      cursor: pointer;
    }

    .footer:hover {
      background: #f8f9fa;
    }

    .footer.syncing wa-icon {
      animation: spin 1s linear infinite;
      color: #0d6efd;
    }

    .footer.offline wa-icon {
      color: #ffc107;
    }

    .footer.error wa-icon {
      color: #dc3545;
    }

    .version {
      padding: 0.4rem 1.25rem 0.6rem;
      font-size: 0.7rem;
      color: #adb5bd;
      text-align: right;
      font-variant-numeric: tabular-nums;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    /* Drawer body padding override + hide default header */
    wa-drawer::part(body) {
      padding: 0;
    }
    wa-drawer::part(header) {
      display: none;
    }

    /* Phone/tablet overlay drawer — cap width at 50vw */
    wa-drawer {
      --size: 50vw;
    }
  `;

  private get isDiabetic() {
    return this.roles.includes("diabetic");
  }
  private get isMonitorOnly() {
    return (
      (this.roles.includes("monitor") || this.roles.includes("clinician")) &&
      !this.roles.includes("diabetic")
    );
  }
  private get isClinician() {
    return this.roles.includes("clinician");
  }
  private get isMonitorOrClinician() {
    return this.roles.includes("monitor") || this.roles.includes("clinician");
  }

  private _navigate(path: string) {
    this.dispatchEvent(
      new CustomEvent("drawer-navigate", {
        detail: { path },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _emit(name: string) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true }));
  }

  private _formatSyncTime(): string {
    if (this.syncStatus === "syncing" || this.syncStatus === "checking") {
      return "Syncing...";
    }
    if (this.syncStatus === "offline") return "Offline";
    if (this.syncStatus === "error") return "Sync error";
    if (!this.lastSyncTime) return "Tap to sync";
    const diff = Math.floor(
      (Date.now() - this.lastSyncTime.getTime()) / 1000,
    );
    if (diff < 60) return "Synced just now";
    if (diff < 3600) return `Synced ${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `Synced ${Math.floor(diff / 3600)}h ago`;
    return `Synced ${this.lastSyncTime.toLocaleDateString()}`;
  }

  private _syncIcon(): string {
    switch (this.syncStatus) {
      case "syncing":
      case "checking":
        return "arrows-rotate";
      case "offline":
        return "wifi-slash";
      case "error":
        return "triangle-exclamation";
      default:
        return this.pendingChanges > 0 ? "cloud-arrow-up" : "cloud";
    }
  }

  private _link(path: string, icon: string, label: string) {
    return html`
      <a
        class=${this.currentPath === path ? "active" : ""}
        @click=${() => this._navigate(path)}
      >
        <wa-icon name=${icon}></wa-icon>
        ${label}
      </a>
    `;
  }

  private _renderContents() {
    return html`
      <div class="brand">
        <button
          class="close-btn"
          @click=${() => this._emit("drawer-close")}
          aria-label="Close menu"
        >
          <wa-icon name="xmark"></wa-icon>
        </button>
        <span>DAFNE Log</span>
      </div>
      <div class="nav-list">
        ${this.isDiabetic
          ? this._link("/support", "comment-dots", "Support")
          : ""}
        ${this.isClinician
          ? this._link("/support-dashboard", "headset", "Support")
          : ""}
        ${!this.isMonitorOnly
          ? this._link("/upload", "cloud-arrow-up", "Import CGM")
          : ""}
        ${!this.isMonitorOnly
          ? this._link("/sample", "clipboard-list", "Sample Data")
          : ""}
        ${this.isDiabetic ? this._link("/share", "share-nodes", "Share Access") : ""}
        ${this.isMonitorOrClinician
          ? this._link("/monitoring", "users", "Monitoring")
          : ""}
        ${this.isAdmin
          ? html`
              <wa-divider></wa-divider>
              ${this._link("/admin", "shield-lock", "Admin")}
            `
          : ""}
        <wa-divider></wa-divider>
        ${this._link("/help", "circle-question", "Help")}
        ${this._link("/diagnostic", "bug", "Diagnostic")}
        <a @click=${() => this._emit("drawer-report-issue")}>
          <wa-icon name="bug"></wa-icon>
          Report an issue
        </a>
      </div>
      <div
        class="footer ${this.syncStatus === "syncing" ||
        this.syncStatus === "checking"
          ? "syncing"
          : ""} ${this.syncStatus === "offline" ? "offline" : ""} ${this
          .syncStatus === "error"
          ? "error"
          : ""}"
        @click=${() => this._emit("drawer-sync")}
      >
        <wa-icon name=${this._syncIcon()}></wa-icon>
        <span>${this._formatSyncTime()}</span>
      </div>
      <div class="version">v${__APP_VERSION__}</div>
    `;
  }

  render() {
    if (this.persistent) {
      return html`<aside class="persistent-shell">${this._renderContents()}</aside>`;
    }
    return html`
      <wa-drawer
        ?open=${this.open}
        placement="start"
        @wa-after-hide=${() => this._emit("drawer-close")}
      >
        ${this._renderContents()}
      </wa-drawer>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-drawer": AppDrawer;
  }
}
